import type {
  AccountingContinuity,
  AccountingExpUpdate,
  AccountingFrame,
  AccountingMesosUpdate,
  GameEvent,
  GameStateSnapshot,
  LiveRateSnapshot,
  MetricKey,
  TrustedLiveRateMetric,
  ValidatedObservation,
} from '@/domain/types'
import type { GamePreset } from '@/presets/types'
import { SourceContinuity, type PresenceMetric } from './source-continuity'
import { performanceDiagnostics } from '@/services/performance-diagnostics'
import { TrustedExpLedger } from './trusted-exp-ledger'

type RateMetric = 'mesos'

const HOUR_MS = 3_600_000
const MAX_TRUSTED_INTERVAL_MS: Readonly<Record<RateMetric, number>> = {
  mesos: 15_000,
}

interface MetricClock {
  lastSourceAt: number | null
  lastActiveAt: number | null
  pendingIntervalMs: number
  trustedDurationMs: number
  updatedAt: number | null
  hasBaseline: boolean
  available: boolean
}

interface ClockObservation {
  readonly continuity: AccountingContinuity
  readonly sourceAt: number | null
}

export interface TrustedMetricLedgerAggregate {
  readonly expGained: number | null
  readonly expTrustedDurationMs: number
  readonly expTrustedPerHour: number | null
  readonly mesosStartBalance: number | null
  readonly mesosCurrentBalance: number | null
  readonly mesosBalanceChange: number | null
  readonly mesosDelta: number | null
  readonly mesosTrustedDurationMs: number
  readonly mesosPerHour: number | null
}

const emptyClock = (): MetricClock => ({
  lastSourceAt: null,
  lastActiveAt: null,
  pendingIntervalMs: 0,
  trustedDurationMs: 0,
  updatedAt: null,
  hasBaseline: false,
  available: false,
})

const emptyClocks = (): Record<RateMetric, MetricClock> => ({
  mesos: emptyClock(),
})

export class TrustedMetricLedger {
  private captureGeneration = 0
  private minimumSourceAt = 0
  private latestCapturedAt = 0
  private suspended = false
  private clocks = emptyClocks()
  private readonly presence = new SourceContinuity()
  private readonly expLedger: TrustedExpLedger

  private mesosBaseline: number | null = null
  private mesosStartBalance: number | null = null
  private mesosCurrentBalance: number | null = null
  private mesosDelta = 0
  private hasMesosBaseline = false
  private mesosContinuityId: number | null = null

  constructor(preset: GamePreset) {
    void preset
    this.expLedger = new TrustedExpLedger()
  }

  reset(at: number, captureGeneration = 0, initialState: GameStateSnapshot | null = null): void {
    this.captureGeneration = captureGeneration
    this.minimumSourceAt = at
    this.latestCapturedAt = at
    this.suspended = false
    this.clocks = emptyClocks()
    this.presence.reset()
    this.expLedger.reset(at, initialState)
    this.mesosBaseline = null
    this.mesosStartBalance = null
    this.mesosCurrentBalance = null
    this.mesosDelta = 0
    this.hasMesosBaseline = false
    this.mesosContinuityId = null
    this.seedInitialState(initialState)
  }

  suspend(): void {
    this.suspended = true
    performanceDiagnostics.breakContinuity('mesos', 'paused')
    this.expLedger.suspend()
    this.rebaselineMetrics(false)
  }

  resume(at: number, captureGeneration: number): void {
    this.suspended = false
    if (captureGeneration !== this.captureGeneration) {
      this.captureGeneration = captureGeneration
      this.minimumSourceAt = at
      this.rebaselineMetrics(false)
    }
    this.expLedger.resume(at)
  }

  changeGeneration(at: number, captureGeneration: number): void {
    if (captureGeneration === this.captureGeneration) return
    this.captureGeneration = captureGeneration
    this.minimumSourceAt = at
    performanceDiagnostics.breakContinuity('mesos', 'generation-changed')
    this.expLedger.changeGeneration(at)
    this.rebaselineMetrics(false)
  }

  ingest(
    state: GameStateSnapshot,
    events: readonly GameEvent[],
    observation: ValidatedObservation | undefined,
    activeAt: number,
    captureGeneration: number,
  ): AccountingFrame {
    this.changeGeneration(state.capturedAt, captureGeneration)
    this.latestCapturedAt = Math.max(this.latestCapturedAt, state.capturedAt)
    if (this.suspended) return this.emptyFrame(state.capturedAt)

    if (observation) {
      const broken = this.presence.observe(observation)
      for (const metric of ['mesos'] as const) {
        if (broken[metric]) {
          this.breakPresence(metric)
          performanceDiagnostics.breakContinuity(metric, broken[metric]!)
        }
      }
    }
    const exp = this.expLedger.ingest(state, events, observation, activeAt)
    const mesos = this.ingestMesos(state, observation, activeAt)
    return { capturedAt: state.capturedAt, captureGeneration: this.captureGeneration, exp, mesos }
  }

  aggregate(): TrustedMetricLedgerAggregate {
    const exp = this.expLedger.aggregate()
    const mesosBalanceChange = this.mesosStartBalance === null || this.mesosCurrentBalance === null
      ? null
      : this.mesosCurrentBalance - this.mesosStartBalance
    return {
      expGained: exp.gained,
      expTrustedDurationMs: exp.trustedDurationMs,
      expTrustedPerHour: exp.perHour,
      mesosStartBalance: this.mesosStartBalance,
      mesosCurrentBalance: this.mesosCurrentBalance,
      mesosBalanceChange,
      mesosDelta: this.hasMesosBaseline ? this.mesosDelta : null,
      mesosTrustedDurationMs: this.clocks.mesos.trustedDurationMs,
      mesosPerHour: this.rate(this.mesosDelta, this.clocks.mesos.trustedDurationMs, this.hasMesosBaseline),
    }
  }

  liveRates(_now = this.latestCapturedAt): Pick<LiveRateSnapshot, 'expTrustedPerHour' | 'mesosPerHour'> {
    return {
      expTrustedPerHour: this.expLedger.liveRate(),
      mesosPerHour: this.liveMetric(this.mesosDelta, this.clocks.mesos),
    }
  }

  private ingestMesos(
    state: GameStateSnapshot,
    observation: ValidatedObservation | undefined,
    activeAt: number,
  ): AccountingMesosUpdate {
    if (this.presence.hasEvidence && this.presence.startAt('mesos') === null) {
      this.breakPresence('mesos')
      return this.emptyMesosUpdate('unavailable')
    }
    if (observation && !observation.accepted.mesos) {
      if (this.presence.hasEvidence) return this.emptyMesosUpdate('pending')
      if (observation.mesos.availability === 'unavailable') {
        this.markUnavailable('mesos')
        this.mesosBaseline = null
        return this.emptyMesosUpdate('unavailable')
      }
      return this.emptyMesosUpdate('pending', observation.mesos.capturedAt)
    }
    if (state.mesos === null) return this.emptyMesosUpdate('ignored')
    const sourceAt = this.metricObservedAt(state, observation, 'mesos')
    if (sourceAt === null || sourceAt < this.minimumSourceAt) return this.emptyMesosUpdate('ignored', sourceAt)
    if (this.presence.hasEvidence && !this.presence.contains('mesos', sourceAt)) return this.emptyMesosUpdate('ignored', sourceAt)
    const continuityId = observation?.mesosContinuityId
    if (continuityId !== undefined
      && (!Number.isSafeInteger(continuityId)
        || continuityId < 0
        || (this.mesosContinuityId !== null && continuityId < this.mesosContinuityId))) {
      return this.emptyMesosUpdate('ignored', sourceAt)
    }
    if (continuityId !== undefined && continuityId !== this.mesosContinuityId) {
      this.mesosContinuityId = continuityId
      this.markUnavailable('mesos')
      this.mesosBaseline = state.mesos
      this.mesosStartBalance ??= state.mesos
      this.mesosCurrentBalance = state.mesos
      this.hasMesosBaseline = true
      this.resetClockBaseline('mesos', sourceAt, activeAt)
      return this.emptyMesosUpdate('baseline', sourceAt, state.mesos)
    }
    const clockObservation = this.observeClock('mesos', sourceAt, activeAt)
    if (clockObservation.continuity === 'ignored') return this.emptyMesosUpdate('ignored', sourceAt)
    if (clockObservation.continuity === 'baseline' || clockObservation.continuity === 'discontinuous' || this.mesosBaseline === null) {
      this.mesosBaseline = state.mesos
      this.mesosStartBalance ??= state.mesos
      this.mesosCurrentBalance = state.mesos
      this.hasMesosBaseline = true
      return this.emptyMesosUpdate(clockObservation.continuity, sourceAt, state.mesos)
    }

    const delta = state.mesos - this.mesosBaseline
    const startValue = this.mesosBaseline
    this.mesosBaseline = state.mesos
    this.mesosCurrentBalance = state.mesos
    this.mesosDelta += delta
    const trustedIntervalMs = this.commitClock('mesos')
    return {
      continuity: 'contiguous',
      sourceAt,
      trustedIntervalMs,
      delta,
      startValue,
      endValue: state.mesos,
    }
  }

  private observeClock(metric: RateMetric, sourceAt: number, activeAt: number): ClockObservation {
    const clock = this.clocks[metric]
    clock.available = true
    clock.updatedAt = sourceAt
    if (!clock.hasBaseline || clock.lastSourceAt === null || clock.lastActiveAt === null) {
      clock.hasBaseline = true
      clock.lastSourceAt = sourceAt
      clock.lastActiveAt = activeAt
      clock.pendingIntervalMs = 0
      return { continuity: 'baseline', sourceAt }
    }
    if (sourceAt <= clock.lastSourceAt) return { continuity: 'ignored', sourceAt }
    const continuousPresence = this.presence.hasEvidence
      && this.presence.contains(metric, clock.lastSourceAt) && this.presence.contains(metric, sourceAt)
    if ((this.presence.hasEvidence && !continuousPresence)
      || (!this.presence.hasEvidence && sourceAt - clock.lastSourceAt > MAX_TRUSTED_INTERVAL_MS[metric])) {
      performanceDiagnostics.breakContinuity(metric, this.presence.hasEvidence ? 'sample-gap' : 'legacy-timeout')
      clock.lastSourceAt = sourceAt
      clock.lastActiveAt = activeAt
      clock.pendingIntervalMs = 0
      return { continuity: 'discontinuous', sourceAt }
    }
    clock.pendingIntervalMs += Math.max(0, activeAt - clock.lastActiveAt)
    clock.lastSourceAt = sourceAt
    clock.lastActiveAt = activeAt
    return { continuity: 'contiguous', sourceAt }
  }

  private commitClock(metric: RateMetric): number {
    const clock = this.clocks[metric]
    const interval = Math.max(0, clock.pendingIntervalMs)
    clock.trustedDurationMs += interval
    clock.pendingIntervalMs = 0
    return interval
  }

  private markUnavailable(metric: RateMetric): void {
    const clock = this.clocks[metric]
    clock.lastSourceAt = null
    clock.lastActiveAt = null
    clock.pendingIntervalMs = 0
    clock.hasBaseline = false
    clock.available = false
  }

  private breakPresence(metric: PresenceMetric): void {
    this.markUnavailable(metric)
    this.mesosBaseline = null
  }

  private rebaselineMetrics(clearAvailability: boolean): void {
    this.presence.reset()
    for (const metric of Object.keys(this.clocks) as RateMetric[]) {
      const clock = this.clocks[metric]
      clock.lastSourceAt = null
      clock.lastActiveAt = null
      clock.pendingIntervalMs = 0
      clock.hasBaseline = false
      if (clearAvailability) clock.available = false
    }
    this.mesosBaseline = null
    this.mesosContinuityId = null
  }

  private seedInitialState(state: GameStateSnapshot | null): void {
    if (!state) return
    const mesosSourceAt = state.observedAt?.mesos ?? state.capturedAt
    if (state.mesos !== null && Number.isSafeInteger(state.mesos) && state.mesos >= 0) {
      this.mesosBaseline = state.mesos
      this.mesosStartBalance = state.mesos
      this.mesosCurrentBalance = state.mesos
      this.hasMesosBaseline = true
      this.resetClockBaseline('mesos', mesosSourceAt, 0)
    }
  }

  private resetClockBaseline(metric: RateMetric, sourceAt: number, activeAt: number): void {
    const clock = this.clocks[metric]
    clock.lastSourceAt = sourceAt
    clock.lastActiveAt = activeAt
    clock.pendingIntervalMs = 0
    clock.hasBaseline = true
    clock.available = true
    clock.updatedAt = sourceAt
  }

  private metricObservedAt(
    state: GameStateSnapshot,
    observation: ValidatedObservation | undefined,
    metric: MetricKey,
  ): number | null {
    if (observation) return observation[metric].capturedAt
    return state.observedAt?.[metric] ?? state.capturedAt
  }

  private liveMetric(total: number, clock: MetricClock, durationMs = clock.trustedDurationMs): TrustedLiveRateMetric {
    if (!clock.available) {
      return { value: null, status: 'unavailable', trustedDurationMs: durationMs, updatedAt: clock.updatedAt }
    }
    const value = durationMs > 0 ? total / durationMs * HOUR_MS : 0
    return {
      value: Number.isFinite(value) ? value : 0,
      status: 'ready',
      trustedDurationMs: durationMs,
      updatedAt: clock.updatedAt,
    }
  }

  private rate(total: number, durationMs: number, hasBaseline: boolean): number | null {
    if (!hasBaseline) return null
    const value = durationMs > 0 ? total / durationMs * HOUR_MS : 0
    return Number.isFinite(value) ? value : 0
  }

  private emptyFrame(capturedAt: number): AccountingFrame {
    return {
      capturedAt,
      captureGeneration: this.captureGeneration,
      exp: this.emptyExpUpdate('ignored'),
      mesos: this.emptyMesosUpdate('ignored'),
    }
  }

  private emptyExpUpdate(continuity: AccountingContinuity): AccountingExpUpdate {
    return {
      continuity,
      sourceAt: null,
      sourceStartAt: null,
      sourceEndAt: null,
      activeStartAt: null,
      activeEndAt: null,
      trustedIntervalMs: 0,
      delta: null,
      exact: false,
      start: null,
      end: null,
    }
  }

  private emptyMesosUpdate(
    continuity: AccountingContinuity,
    sourceAt: number | null = null,
    value: number | null = null,
  ): AccountingMesosUpdate {
    return { continuity, sourceAt, trustedIntervalMs: 0, delta: null, startValue: null, endValue: value }
  }


}
